'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { NavigationItem } from '@/components/site/SiteHeader'
import { ordinal } from '@/lib/portfolio'

/**
 * The narrow-screen menu: the same five sections, full screen.
 *
 * It closes on a route change rather than on the link's own click, because a
 * click on the link you are already on does not navigate — and a menu that
 * stays open in that case reads as a broken tap.
 */
export function MobileMenu({ items, email }: { items: NavigationItem[]; email: string }) {
    const [open, setOpen] = useState(false)
    const pathname = usePathname()

    useEffect(() => setOpen(false), [pathname])

    // A full-screen overlay over a scrollable page scrolls the page behind it.
    useEffect(() => {
        if (!open) {
            return
        }

        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = previous
        }
    }, [open])

    useEffect(() => {
        if (!open) {
            return
        }

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false)
            }
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Menu"
                aria-expanded={open}
                className="-mr-2.5 hidden cursor-pointer flex-col items-end gap-[5px] border-0 bg-transparent p-2.5 text-fg max-lg:flex"
            >
                <span className="block h-[1.5px] w-[22px] bg-current" />
                <span className="block h-[1.5px] w-[14px] bg-current" />
            </button>

            {open
                ? createPortal(
                      /*
                       * Rendered on the body rather than where it is written.
                       *
                       * The header carries `backdrop-blur`, and a
                       * `backdrop-filter` makes its box the containing block
                       * for `fixed` descendants — so `inset-0` resolved to the
                       * header's own 64 pixels and the panel covered the bar
                       * and nothing else. The page went on showing through
                       * behind the menu, which read as a missing background.
                       */
                      <div className="fixed inset-0 z-95 flex animate-rise-in flex-col bg-bg">
                          <div className="flex h-16 items-center justify-end border-b border-hair px-6">
                              <button
                                  type="button"
                                  onClick={() => setOpen(false)}
                                  aria-label="Sluiten"
                                  className="-mr-2.5 cursor-pointer border-0 bg-transparent p-2.5 text-[22px] leading-none text-fg"
                              >
                                  ×
                              </button>
                          </div>
                          <nav className="flex flex-1 flex-col py-2">
                              {[...items, { label: 'Contact', url: '/contact' }].map((item, index) => (
                                  <Link
                                      key={item.url}
                                      href={item.url}
                                      data-cta={item.url === '/contact' ? 'menu' : undefined}
                                      onClick={() => setOpen(false)}
                                      className="flex items-baseline gap-[14px] border-b border-hair px-6 py-[22px] text-[28px] tracking-[-0.03em]"
                                  >
                                      <span className="font-mono text-[11px] text-faint">
                                          {ordinal(index)}
                                      </span>
                                      {item.label}
                                  </Link>
                              ))}
                          </nav>
                          {email === '' ? null : (
                              <div className="border-t border-hair p-6">
                                  <a href={`mailto:${email}`} className="font-mono text-xs text-dim">
                                      {email}
                                  </a>
                              </div>
                          )}
                      </div>,
                      document.body,
                  )
                : null}
        </>
    )
}
